// Keep the initial booking bundle free of the PostHog SDK. Events are buffered
// until it loads, so an early form tap is still captured.
const GA_ID = 'G-X7RLCKC432';
const JOURNEY_KEY = 'ig_journey';
const MAX_PENDING_EVENTS = 100;

type PostHogClient = typeof import('posthog-js').default;
type PendingEvent =
  | { kind: 'pageview'; path: string; title?: string; journeyId: string }
  | { kind: 'event'; action: string; category?: string; label?: string; value?: number; journeyId: string }
  | { kind: 'identify'; email: string; properties?: Record<string, unknown> };

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

let posthogClient: PostHogClient | null = null;
let posthogLoading: Promise<void> | null = null;
let pendingEvents: PendingEvent[] = [];
let fallbackJourneyId: string | null = null;
let gaInitialized = false;

function createJourneyId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getJourneyId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = sessionStorage.getItem(JOURNEY_KEY);
    if (existing) return existing;
    const created = createJourneyId();
    sessionStorage.setItem(JOURNEY_KEY, created);
    return created;
  } catch {
    // Storage can be disabled in private browsing. Preserve one ID in memory.
    fallbackJourneyId ??= createJourneyId();
    return fallbackJourneyId;
  }
}

function isMeta2QaSession(): boolean {
  if (typeof window === 'undefined' || window.location.pathname !== '/book-demo/meta2') return false;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
  try {
    if (new URLSearchParams(window.location.search).get('qa') === '1') {
      sessionStorage.setItem('ig_meta2_qa', '1');
      return true;
    }
    return sessionStorage.getItem('ig_meta2_qa') === '1';
  } catch {
    return new URLSearchParams(window.location.search).get('qa') === '1';
  }
}

function ga(): Window['gtag'] | null {
  if (typeof window === 'undefined') return null;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args: unknown[]) => { window.dataLayer.push(args); };
  }
  if (!gaInitialized) {
    gaInitialized = true;
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: false, transport_type: 'beacon' });
  }
  return window.gtag;
}

function capturePostHog(event: PendingEvent): void {
  if (!posthogClient) return;
  try {
    if (event.kind === 'pageview') {
      posthogClient.capture('$pageview', {
        // Page path only; query parameters can contain email or phone data.
        $current_url: window.location.origin + event.path,
        journey_id: event.journeyId,
        ...(event.title ? { page_title: event.title } : {}),
      });
    } else if (event.kind === 'event') {
      posthogClient.capture(event.action, {
        journey_id: event.journeyId,
        ...(event.category ? { event_category: event.category } : {}),
        ...(event.label ? { event_label: event.label } : {}),
        ...(event.value !== undefined ? { value: event.value } : {}),
      });
    } else {
      posthogClient.identify(event.email, { email: event.email, ...event.properties });
    }
  } catch { /* Analytics must not interrupt the booking flow. */ }
}

function queuePostHog(event: PendingEvent): void {
  if (posthogClient) {
    capturePostHog(event);
    return;
  }
  if (pendingEvents.length >= MAX_PENDING_EVENTS) pendingEvents.shift();
  pendingEvents.push(event);
}

export function initPostHog(): void {
  if (typeof window === 'undefined' || posthogClient || posthogLoading) return;
  if (isMeta2QaSession()) return;
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
  if (!apiKey) return;

  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.posthog.com';
  posthogLoading = import('posthog-js').then(({ default: client }) => {
    client.init(apiKey, {
      api_host: host,
      capture_pageview: false,
      capture_pageleave: true,
    });
    posthogClient = client;
    for (const event of pendingEvents) capturePostHog(event);
    pendingEvents = [];
  }).catch(() => {
    // A later navigation can retry initialization.
    posthogLoading = null;
  });
}

export function trackPageView(path: string, title?: string): void {
  if (typeof window === 'undefined') return;
  // GA and PostHog receive a path without the query: URLs may contain PII.
  const safePath = path.split('?')[0];
  const journeyId = getJourneyId();
  if (isMeta2QaSession()) return;
  ga()?.('event', 'page_view', {
    page_path: safePath,
    page_title: title,
    journey_id: journeyId,
    send_to: GA_ID,
  });
  queuePostHog({ kind: 'pageview', path: safePath, title, journeyId });
}

export function trackEvent(action: string, category?: string, label?: string, value?: number): void {
  if (typeof window === 'undefined') return;
  const journeyId = getJourneyId();
  if (!isMeta2QaSession()) {
    ga()?.('event', action, {
      event_category: category,
      event_label: label,
      value,
      journey_id: journeyId,
    });
    queuePostHog({ kind: 'event', action, category, label, value, journeyId });
  }
  const path = window.location.pathname;
  const isMetaBookingEvent = path === '/book-demo/meta' &&
    /^(booking_[a-z_]{1,50}|demo_booking_[a-z_]{1,50})$/.test(action);
  const isMeta2BookingEvent = path === '/book-demo/meta2' && /^meta2_[a-z_]{1,50}$/.test(action);
  if (isMetaBookingEvent || isMeta2BookingEvent) {
    const body = JSON.stringify({ journey_id: journeyId, event_name: action, event_category: category, event_label: label, path, is_qa: isMeta2BookingEvent && isMeta2QaSession() });
    if (typeof navigator.sendBeacon === 'function') {
      try {
        if (navigator.sendBeacon('/api/booking-event', new Blob([body], { type: 'application/json' }))) return;
      } catch { /* Use fetch below. */ }
    }
    fetch('/api/booking-event', {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {});
  }
}

export function identifyUser(email: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  queuePostHog({ kind: 'identify', email, properties });
}
